import React, { useState, useEffect } from 'react';
import { Card, CardBody } from '../../../../Components/Card';
import Button from '../../../../Components/Button';
import PasswordStrengthChecker from '../../../../Components/PasswordStrengthChecker';
import Validator from '../../../../Components/Validator';
import useAxiosCall from '../../../../Hooks/useAxiosCall';
import { SuccessAlert, ErrorAlert } from '../../../../Components/Alert';
import useLogout from '../../../../Hooks/useLogout';
import { encryptData } from '../../../../Utilities/CryptoUtility';
import PasswordInput from '../../../../Components/PasswordInput';
import { SecureStorage } from '../../../../Utilities/StorageEncryption';
const ChangePasswordForm = () => {
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [alert, setAlert] = useState({ type: '', message: '' });
    const [updated, setUpdated] = useState(false);
    const [hasTyped, setHasTyped] = useState(false);
    const { handleLogout } = useLogout();
    const [passwordChecks, setPasswordChecks] = useState({
        minLength: false,
        maxLength: false,
        upperCase: false,
        number: false,
        specialChar: false,
    });

    const validator = Validator();

    useEffect(() => {
        // Cleanup function for component unmount
        return () => {
            resetForm();
        };
    }, []);

    const handleInputChange = event => {
        const { name, value } = event.target;

        let newPasswordChecks = {};
        if (name === "newPassword") {
            setHasTyped(true);
            newPasswordChecks = {
                minLength: value.length > 7,
                maxLength: (value.length <= 20),
                upperCase: /[a-z]/.test(value) && /[A-Z]/.test(value),
                number: /\d/.test(value),
                specialChar: /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value)
            };
            setPasswordChecks(newPasswordChecks);
        }
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setAlert({ type: '', message: '' });
        setHasTyped(false);
        setPasswordChecks({
            minLength: false,
            maxLength: false,
            upperCase: false,
            number: false,
            specialChar: false,
        });
    };

    const handleApiCallSuccess = (data) => {
        setAlert({ type: 'success', message: data.message });
        setTimeout(() => {
            handleLogout();
        }, 1500)
    };

    const handleApiCallError = (errorMessage) => {
        setAlert({ type: 'error', message: errorMessage });
    };

    const { makeApiCall } = useAxiosCall();

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validator.allValid()) {
            try {
                const requestData = {
                    data: encryptData(JSON.stringify(
                        {
                            oldPassword: formData.oldPassword,
                            newPassword: formData.newPassword,
                            confirmPassword: formData.confirmPassword,
                            domain: SecureStorage.getItem('domain'),
                        }
                    ))
                };

                await makeApiCall({
                    requestData,
                    onSuccess: handleApiCallSuccess,
                    onError: handleApiCallError,
                    requestType: 'POST',
                    url: 'changePassword',
                });
            } catch (error) {
                console.error('API call error:', error);
            }
        } else {
            validator.showMessages();
            setUpdated(!updated);
        }
    };
    return (
        <Card>
            {alert.type === 'success' && <SuccessAlert success={true} messages={[alert.message]} resetAlert={setAlert} />}
            {alert.type === 'error' && <ErrorAlert error={true} messages={[alert.message]} resetAlert={setAlert} />}
            <CardBody>
                <form onSubmit={handleSubmit} noValidate>
                    <p className="h5 text-center mb-4">Change Password</p>
                    <FieldWrapper>
                        <PasswordInput
                            id="oldPassword"
                            name="oldPassword"
                            label="Old password"
                            value={formData.oldPassword}
                            onChange={handleInputChange}
                        />
                        {validator.message('oldPassword', formData.oldPassword, 'oldPasswordRequired|passwordLength')}
                    </FieldWrapper>

                    <FieldWrapper>
                        <PasswordInput
                            id="newPassword"
                            name="newPassword"
                            label="New password"
                            value={formData.newPassword}
                            onChange={handleInputChange}
                        />
                        {validator.message('newPassword', formData.newPassword, 'newPasswordRequired|passwordLength')}
                        {hasTyped && validator.message('newPassword', formData.newPassword, 'passwordStrength')}
                        {hasTyped && <PasswordStrengthChecker checks={passwordChecks} marginRequired />}
                    </FieldWrapper>

                    <FieldWrapper>
                        <PasswordInput
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Confirm password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                        />
                        {validator.message('confirmPassword', formData.confirmPassword, 'confirmPasswordRequired|passwordLength')}
                        {validator.message('passwords', formData, 'passwordsMatch')}
                    </FieldWrapper>

                    <Button type="submit">Change Password</Button>
                </form>
            </CardBody>
        </Card>
    );
};

const FieldWrapper = ({ children }) => (
    <div className="mb-4">{children}</div>
);

export default ChangePasswordForm;
